// BUILD-046 correction cycle 1bb88b3e (stage B1, item N1): the small task search of the toolbar, scoped to the CURRENT view.
//
// WHAT IT SEARCHES. Only the tasks the view has already loaded for the Caller, and only text the Caller is already shown for them: the title, the
// assignee (owner) name, the requester name, the recipient label and the group / person names of a shared task. It asks the server for nothing, so it
// can never reveal a task, a person or a count beyond the Caller's scope. A task whose restricted source the Caller cannot open (`redacted`) is searched
// by its static title only, never by the fields behind it.
//
// WHAT IT SAYS. A list that is read page by page (completed Team Work, the issued requests) may hold more than is loaded. A search over the loaded part
// is then NOT a search of everything, and the view must not present it as one: `searchScopeText` says "Searching the N loaded tasks; load more to search
// all." until the last page is loaded. `complete` comes from the list (pagedListModel: no `next` cursor); a list that arrives whole is complete.
//
// MATCHING. Case-insensitive; the query is split on spaces and EVERY word must appear in the task's text (so "ana pool" finds "Ana" on "Pool tiles"). A blank
// query matches everything and is reported as inactive.
//
// Pure module. Erasable TypeScript only; no imports.

export interface SearchableTask {
  title?: unknown;
  ownerName?: unknown;
  requesterName?: unknown;
  recipientLabel?: unknown;
  groupRefs?: readonly { name?: unknown }[];
  recipientSummary?: readonly { name?: unknown }[];
  redacted?: unknown;
}

function strings(values: readonly unknown[]): string[] {
  const out: string[] = [];
  for (const value of values) if (typeof value === 'string' && value.trim() !== '') out.push(value.trim());
  return out;
}

/** The texts of one task that a search may match (see the header). A redacted task offers its static title only. */
export function taskSearchText(task: SearchableTask): string[] {
  if (task.redacted === true) return strings([task.title]);
  return strings([
    task.title,
    task.ownerName,
    task.requesterName,
    task.recipientLabel,
    ...(Array.isArray(task.groupRefs) ? task.groupRefs.map((g) => g?.name) : []),
    ...(Array.isArray(task.recipientSummary) ? task.recipientSummary.map((r) => r?.name) : []),
  ]);
}

/** The texts of one "Requests I issued" row: the title, the group names, the requester, the assignee and the recipients the Caller may read. */
export function issuedSearchText(row: {
  title?: unknown;
  groupNames?: readonly unknown[];
  requester?: { displayName?: unknown } | null;
  task?: { ownerName?: unknown; redacted?: unknown } | null;
  members?: readonly { displayName?: unknown }[];
}): string[] {
  if (row.task?.redacted === true) return strings([row.title]);
  return strings([row.title, ...(row.groupNames ?? []), row.requester?.displayName, row.task?.ownerName, ...(row.members ?? []).map((m) => m.displayName)]);
}

/** The words of a query, lower-cased. */
export function queryWords(query: unknown): string[] {
  if (typeof query !== 'string') return [];
  return query.toLowerCase().split(/\s+/).filter((word) => word !== '');
}

/** True when every word of the query appears in the texts. A query with no words matches everything. */
export function matchesQuery(texts: readonly string[], query: unknown): boolean {
  const words = queryWords(query);
  if (words.length === 0) return true;
  const haystack = texts.join('\n').toLowerCase();
  return words.every((word) => haystack.includes(word));
}

export interface SearchResult<T> {
  /** The loaded items that match, in their loaded order (the sort is applied by the view). Every loaded item when the query is blank. */
  visible: readonly T[];
  /** How many items the view has loaded: the search ran over exactly these. */
  loadedCount: number;
  /** True when nothing more can be loaded, so the search covered everything the Caller may see in this view. */
  complete: boolean;
  /** The query was not blank. */
  active: boolean;
}

export interface SearchOptions<T> {
  /** True when the list is whole (it has no further page). */
  complete: boolean;
  /** The texts to match for one item. Default: taskSearchText. */
  textOf?: (item: T) => string[];
}

export function searchLoaded<T>(items: readonly T[], query: unknown, options: SearchOptions<T>): SearchResult<T> {
  const active = queryWords(query).length > 0;
  const textOf = options.textOf ?? ((item: T) => taskSearchText(item as unknown as SearchableTask));
  return {
    visible: active ? items.filter((item) => matchesQuery(textOf(item), query)) : items,
    loadedCount: items.length,
    complete: options.complete === true,
    active,
  };
}

export interface SearchNoun {
  singular: string;
  plural: string;
}

const TASKS: SearchNoun = { singular: 'task', plural: 'tasks' };

/**
 * The sentence that keeps a search truthful. Null while the query is blank. While more can be loaded: "Searching the N loaded tasks; load more to search
 * all." Once everything is loaded: "M of N tasks match."
 */
export function searchScopeText(result: SearchResult<unknown>, noun: SearchNoun = TASKS): string | null {
  if (!result.active) return null;
  const word = (n: number): string => (n === 1 ? noun.singular : noun.plural);
  if (!result.complete) return `Searching the ${result.loadedCount} loaded ${word(result.loadedCount)}; load more to search all.`;
  return `${result.visible.length} of ${result.loadedCount} ${word(result.loadedCount)} match.`;
}

// ───────────────────────── one query per view ─────────────────────────

export type SearchViewKey = 'my-work' | 'group-shared' | 'team-work';

/** The query typed in each view. A query belongs to the view it was typed in: it never filters another view. */
export interface ViewSearchState {
  readonly queries: Readonly<Record<SearchViewKey, string>>;
}

export function initialViewSearch(): ViewSearchState {
  return { queries: { 'my-work': '', 'group-shared': '', 'team-work': '' } };
}

export function searchOf(state: ViewSearchState, view: SearchViewKey): string {
  return state.queries[view] ?? '';
}

export function setViewSearch(state: ViewSearchState, view: SearchViewKey, query: string): ViewSearchState {
  if (typeof query !== 'string' || state.queries[view] === undefined || state.queries[view] === query) return state;
  return { queries: { ...state.queries, [view]: query } };
}

/** A Caller change clears every query (what the previous Caller was looking for is not carried to the next). */
export function resetViewSearch(): ViewSearchState {
  return initialViewSearch();
}
